import React, { useState, useEffect } from 'react'
import { Container, Title, Switch, Button, Dialog, Input } from "@bsf/force-ui";
import { __, sprintf } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { Toaster, toast } from 'react-hot-toast';

const Editor = () => {
  const [smoothScrollEnabled, setSmoothScrollEnabled] = useState(false);
  const [smoothScrollOptions, setSmoothScrollOptions] = useState({
    step: '80',
    speed: '480'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [step, setStep] = useState('80');
  const [speed, setSpeed] = useState('480');
  const [combinedCssEnabled, setCombinedCssEnabled] = useState(false);
  const [combinedJsEnabled, setCombinedJsEnabled] = useState(false);
  const [analyticsOptin, setAnalyticsOptin] = useState(false);

  // Fetch initial settings on component mount
  useEffect(() => {
    fetchSmoothScrollStatus();
    fetchSmoothScrollSettings();
    fetchCombinedCssSettings();
    fetchCombinedJsSettings();
    fetchAnalyticsOptin();
  }, []);

  const fetchCombinedJsSettings = () => {
    try {
      // Make the API call
      apiFetch({
        path: '/ultimate-vc/v1/combined-js',
        method: 'GET'
      }).then(response => {
        // Update state with the response data
        if (response) {
          setCombinedJsEnabled(response.enabled === 'enable');
        }
      });
    } catch (error) {
      console.error('Error fetching combined JS settings:', error);
    }
  };

  const fetchAnalyticsOptin = () => {
    try {
      apiFetch({
        path: '/ultimate-vc/v1/analytics-optin',
        method: 'GET'
      }).then(response => {
        if (response) {
          setAnalyticsOptin(response.enabled === 'yes');
        }
      });
    } catch (error) {
      console.error('Error fetching analytics optin:', error);
    }
  };

  const fetchSmoothScrollStatus = () => {
    apiFetch({
      path: '/ultimate-vc/v1/smooth-scroll-status',
      method: 'GET'
    })
      .then(response => {
        if (response) {
          setSmoothScrollEnabled(response.enabled === 'enable');
        }
      })
      .catch(error => {
        console.error('Error fetching smooth scroll status:', error);
      });
  };

  const fetchSmoothScrollSettings = () => {
    setIsLoading(true);
    apiFetch({
      path: '/ultimate-vc/v1/smooth-scroll-settings',
      method: 'GET'
    })
      .then((response) => {
        if (response) {
          setSmoothScrollEnabled(response.enabled === 'enable');

          if (response.options) {
            setSmoothScrollOptions(response.options);

            if (response.options.step) {
              setStep(response.options.step);
            }

            if (response.options.speed) {
              setSpeed(response.options.speed);
            }
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching smooth scroll settings:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCombinedCssSettings = () => {
    try {
      // Make the API call
      apiFetch({
        path: '/ultimate-vc/v1/combined-css',
        method: 'GET'
      }).then(response => {
        // Update state with the response data
        if (response) {
          setCombinedCssEnabled(response.enabled === 'enable');
        }
      });
    } catch (error) {
      console.error('Error fetching combined CSS settings:', error);
    }
  };

  const handleSmoothScrollSwitch = () => {
    try {
      const newStatus = !smoothScrollEnabled;
      setSmoothScrollEnabled(newStatus);

      apiFetch({
        path: '/ultimate-vc/v1/smooth-scroll-settings',
        method: 'POST',
        data: {
          enabled: newStatus ? 'enable' : 'disable'
        }
      });

      // If turning off smooth scroll, reset the input fields and show a toast
      if (!newStatus) {
        setStep('80');
        setSpeed('480');
        toast.success('Smooth scroll has been disabled and settings have been removed.');
      }
    } catch (error) {
      console.error('Error updating smooth scroll status:', error);
      setSmoothScrollEnabled(!smoothScrollEnabled); // Revert on error
    }
  };

  const handleCombinedCssSwitch = () => {
    try {
      const newStatus = !combinedCssEnabled;
      setCombinedCssEnabled(newStatus);

      apiFetch({
        path: '/ultimate-vc/v1/combined-css',
        method: 'POST',
        data: {
          enabled: newStatus ? 'enable' : 'disable'
        }
      }).then(() => {
        // Always show a toast when the switch is toggled
        if (newStatus) {
          toast.success('Combined CSS activated. A combined file for all CSS modules will be loaded on your website.');
        } else {
          toast.success('Combined CSS deactivated. Individual CSS files will be loaded as needed.');
        }
      });
    } catch (error) {
      console.error('Error updating combined CSS status:', error);
      setCombinedCssEnabled(!combinedCssEnabled); // Revert on error
      toast.error('Error updating Combined CSS settings. Please try again.');
    }
  };

  const handleCombinedJsSwitch = () => {
    try {
      const newStatus = !combinedJsEnabled;
      setCombinedJsEnabled(newStatus);

      apiFetch({
        path: '/ultimate-vc/v1/combined-js',
        method: 'POST',
        data: {
          enabled: newStatus ? 'enable' : 'disable'
        }
      }).then(() => {
        // Always show a toast when the switch is toggled
        if (newStatus) {
          toast.success('Combined JS activated. A combined file for all JS modules will be loaded on your website.');
        } else {
          toast.success('Combined JS deactivated. Individual JS files will be loaded as needed.');
        }
      });
    } catch (error) {
      console.error('Error updating combined JS status:', error);
      setCombinedJsEnabled(!combinedJsEnabled); // Revert on error
      toast.error('Error updating Combined JS settings. Please try again.');
    }
  };

  const handleAnalyticsSwitch = () => {
    try {
      const newStatus = !analyticsOptin;
      setAnalyticsOptin(newStatus);

      apiFetch({
        path: '/ultimate-vc/v1/analytics-optin',
        method: 'POST',
        data: {
          enabled: newStatus ? 'yes' : 'no'
        }
      }).then(() => {
        if (newStatus) {
          toast.success('Help has been enabled. Thanks for helping us improve!');
        } else {
          toast.success('Help has been disabled. We appreciate your time');
        }
      });
    } catch (error) {
      console.error('Error updating analytics optin:', error);
      setAnalyticsOptin(!analyticsOptin);
      toast.error('Error updating Help. Please try again');
    }
  };

  const handleStepChange = (e) => {
    setStep(e.target.value);
  };

  const handleSpeedChange = (e) => {
    setSpeed(e.target.value);
  };

  const handleSaveSettings = () => {
    try {
      const settings = {
        step: step,
        speed: speed
      };

      apiFetch({
        path: '/ultimate-vc/v1/smooth-scroll-options',
        method: 'POST',
        data: settings
      });

      setSmoothScrollOptions(settings);
      toast.success('Smooth scroll settings saved successfully!');
    } catch (error) {
      console.error('Error saving smooth scroll options:', error);
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        containerStyle={{
          top: 20,
          right: 20,
          marginTop: '40px',
        }}
        toastOptions={{
          duration: 1000,
          style: {
            color: '#0017E1',
          },
          success: {
            duration: 2000,
            style: {
              color: '',
            },
            iconTheme: {
              primary: '#0017E1',
              secondary: '#fff',
            },
          },
        }}
      />
      <Title
        description=""
        icon={null}
        iconPosition="right"
        size="sm"
        tag="h2"
        title={__('General Settings', 'ultimate_vc')}
      />
      <div
        className="box-border bg-background-primary p-6 rounded-lg"
        style={{
          marginTop: "24px",
        }}
      >
        <div className="relative">
          <Container
            align="center"
            className="flex flex-col lg:flex-row"
            containerType="flex"
            direction="column"
            gap="sm"
            justify="between"
            item
          >
            <Container.Item className="shrink flex flex-col space-y-1">
              <div className="text-base font-semibold m-0 mb-2">
                {__('Smooth Scroll', 'ultimate_vc')}
              </div>
              <div className="text-sm font-normal m-0" style={{ maxWidth: '90%', color: "#9CA3AF" }}>
                {sprintf(
                  __(
                    'The smooth scroll will change the built-in scroll effect of browsers. It is mostly used in combination with parallax effects.',
                    'ultimate_vc'
                  ),
                )}
              </div>

              {smoothScrollEnabled && (

                <div className="flex flex-wrap items-end gap-4" style={{ marginTop: '24px' }}>
                  <div className="">
                    <label className="block text-md font-medium mb-1">
                      {__('Step', 'ultimate_vc')}
                    </label>
                    <input
                      type="number"
                      value={step}
                      onChange={handleStepChange}
                      placeholder="80"
                      style={{
                        borderColor: '#e0e0e0', // Default border color
                        outline: 'none',       // Removes the default outline
                        boxShadow: 'none',     // Removes the default box shadow
                        marginTop: '8px'
                      }}
                      className="w-60 border  rounded-md border-gray-300 pt-2  px-3 py-2"
                    />
                  </div>

                  <div className="w-32">
                    <label className="block text-md font-medium mb-1">
                      {__('Scroll Speed', 'ultimate_vc')}
                    </label>
                    <input
                      type="number"
                      value={speed}
                      onChange={handleSpeedChange}
                      style={{
                        borderColor: '#e0e0e0', // Default border color
                        outline: 'none',       // Removes the default outline
                        boxShadow: 'none',     // Removes the default box shadow
                        marginTop: '8px',
                      }}
                      placeholder="480"
                      className="w-60 border rounded-md border-gray-300  px-3 py-2"
                    />
                  </div>
                  <Button
                    variant="primary"
                    className="bg-[#0017E1] px-3 py-2 uavc-remove-ring"
                    style={{
                      backgroundColor: "#0017E1",
                      transition: "background-color 0.3s ease",
                      height: '46px',
                    }}
                    onClick={handleSaveSettings}
                  >
                    {__("Save Changes", "ultimate_vc")}
                  </Button>
                </div>
              )}
            </Container.Item>
            <Container.Item
              className="shrink-0 p-2 flex space-y-6 uavc-remove-ring"
              alignSelf="flex-start"
              order="none"
              style={{
                position: 'absolute',
                right: '0',
                top: '0'
              }}
            >
              <Switch
                size="md"
                value={smoothScrollEnabled}
                onChange={handleSmoothScrollSwitch}
                disabled={isLoading}
              />
            </Container.Item>
          </Container>
        </div>

        <hr className="w-full border-b-0 border-x-0 border-t border-solid border-t-border-subtle" style={{ marginTop: '34px', marginBottom: '34px', borderColor: '#E5E7EB' }} />

        <Container
          align="center"
          className="flex flex-col lg:flex-row"
          containerType="flex"
          direction="column"
          gap="sm"
          justify="between"
          item
        >

          <Container.Item className="shrink flex flex-col space-y-1">
            <div className="text-base font-semibold m-0 mb-2">
              {__('Combined CSS', 'ultimate_vc')}
            </div>
            <div style = {{ color: "#9CA3AF"}} className="text-sm font-normal m-0">
              {sprintf(
                __(
                  'If optimized option is set to ON, a combined file for all CSS modules in our plugin will be loaded on your website. Use this option if you use Ultimate Addons significantly on your website.',
                  'ultimate_vc'
                ),
              )}
            </div>
          </Container.Item>
          <Container.Item
            className="shrink-0 p-2 flex space-y-6 uavc-remove-ring"
            alignSelf="auto"
            order="none"
            style={{ marginTop: '40px' }}
          >
            <Switch
              size="md"
              value={combinedCssEnabled}
              onChange={handleCombinedCssSwitch}
            />
          </Container.Item>
        </Container>

        <hr className="w-full border-b-0 border-x-0 border-t border-solid border-t-border-subtle" style={{ marginTop: '34px', marginBottom: '34px', borderColor: '#E5E7EB' }} />

        <Container
          align="center"
          className="flex flex-col lg:flex-row"
          containerType="flex"
          direction="column"
          gap="sm"
          justify="between"
          item
        >

          <Container.Item className="shrink flex flex-col space-y-1">
            <div className="text-base font-semibold m-0 mb-2">
              {__('Combined JS', 'ultimate_vc')}
            </div>
            <div style = {{ color: "#9CA3AF"}} className="text-sm font-normal m-0">
              {sprintf(
                __(
                  'If optimized option is set to ON, a combined file for all JS modules in our plugin will be loaded on your website. Use this option if you use Ultimate Addons significantly on your website.',
                  'ultimate_vc'
                ),
              )}
            </div>
          </Container.Item>
          <Container.Item
            className="shrink-0 p-2 flex space-y-6 uavc-remove-ring"
            alignSelf="auto"
            order="none"
            style={{ marginTop: '40px' }}
          >
            <Switch
              size="md"
              value={combinedJsEnabled}
              onChange={handleCombinedJsSwitch}
            />
          </Container.Item>
        </Container>

        <hr className="w-full border-b-0 border-x-0 border-t border-solid border-t-border-subtle" style={{ marginTop: '34px', marginBottom: '34px', borderColor: '#E5E7EB' }} />

        <Container
          align="center"
          className="flex flex-col lg:flex-row"
          containerType="flex"
          direction="column"
          gap="sm"
          justify="between"
          item
        >
          <Container.Item className="shrink flex flex-col space-y-1">
            <div className="text-base font-semibold m-0 mb-2">
              {__('Help us Improve your experience', 'ultimate_vc')}
            </div>
            <div style = {{ color: "#9CA3AF"}} className="text-sm font-normal m-0">
              {__(
                'Collect non-sensitive information from your website, such as the PHP version and features used, to help us fix bugs faster, make smarter decisions, and build features that actually matter to you. ',
                'ultimate_vc'
              )}
              <a
                href="https://store.brainstormforce.com/usage-tracking/?utm_source=wp_dashboard&utm_medium=general_settings&utm_campaign=usage_tracking"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0017E1' }}
                className="no-underline"
              >
                {__('Learn more', 'ultimate_vc')}
              </a>
            </div>
          </Container.Item>
          <Container.Item
            className="shrink-0 p-2 flex space-y-6 uavc-remove-ring"
            alignSelf="auto"
            order="none"
            style={{ marginTop: '40px' }}
          >
            <Switch size="md" value={analyticsOptin} onChange={handleAnalyticsSwitch} />
          </Container.Item>
        </Container>
      </div>
    </>
  )
}

export default Editor
